import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import React, { useContext, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '.././../../../common/ConstantsLagoon';
import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import { CardWrapperDetailsPage } from '../../../../common/CardWrapper';
import {
  AdobeEventMethods,
  Constants,
  LAWW_USER_TIER2,
  ProviderTypes,
} from '../../../../common/Constants';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { DetailsPageHeader } from '../../../../common/DetailsPageHeader/DetailsPageHeader';
import { LoadingSpinnerComp } from '../../../../common/Loader';
import { ProviderDetailTabs } from '../../../../common/ProviderDetailTabs/ProviderDetailTabs';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { SendFeedback } from '../../../../common/SendFeedback/SendFeedback';
import {
  convertObjectToUrlParams,
  getFeatureFlags,
} from '../../../../common/Utils';
import {
  getSearchLocation,
  internalConvertProviderTypeToAdobeType,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useInitializeStore } from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useProviderDetailsData } from '../../../../hooks/useProviderDetailsData';
import { useProviderResultsV2 } from '../../../../hooks/useProviderSearch';
import { useProviderSearchTelemetry } from '../../../../hooks/useProviderSearchTelemetry';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { ProviderType } from '../../../../models/ProviderDetails';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { getAiBioEligibility } from '../../../../utils/getAiBioEligibility.utils';
import { getStateFromSearchBar } from '../../../../utils/locationSelectors';
import {
  getNetworkStatus,
  getSelectedLocationIndex,
  isDoLookupByPcpId,
} from '../../../../utils/providerDetails.utils';
import { getNameAttrs } from '../../../../utils/providerNames.utils';
import {
  getCurrentMember,
  getPolicyNumber,
} from '../../../../utils/user.utils';
import { concatenateStrings, getTelemetryCoverageType } from '../MapView/utils';

export const ProviderDetailsView = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const { navigate, getLocation } = useRouter();
  const location = getLocation();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType, chipValue } = chipStore;

  const { searchTerm, searchFromTypeAhead, searchType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      searchTerm: state.typeaheadSearchStore.searchTerm,
      searchFromTypeAhead: state.typeaheadSearchStore.searchFromTypeAhead,
      searchType: state.typeaheadSearchStore.searchType,
    }))
  );

  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );

  const {
    providerId: storeProviderId,
    locationId,
    source,
  } = useInitializeStore(ConstantsRoutes.PROVIDER_DETAILS.key);

  const {
    avatar = 'heart',
    showDirection,
    isTieredSnackCard,
    isTieredDataCard,
    providerId,
    data,
    isLoading,
  } = useProviderDetailsData();

  const isTieredDataCardValue = isTieredDataCard;

  const { dependentSeqNbr } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  const [navigateToDirections, setNavigateToDirections] = useState(
    Boolean(showDirection)
  );
  const [isMapView, setIsMapView] = useState<boolean>(false);
  const [hasQueried, setHasQueried] = useState<boolean>(false);
  const [triggerScroll, setTriggerScroll] = useState<boolean>(false);
  const storedLocation = getGeoLocationFromStorage();
  const preferencesData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);
  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();
  const { navigateToSectionId } = location.state || {};
  const currentMember = getCurrentMember();
  const [lookUpByPcpIdEnabled, setLookUpByPcpIdEnabled] = useState(false);

  useEffect(() => {
    if (navigateToSectionId) {
      setTriggerScroll(true);
    }
  }, [navigateToSectionId]);

  useEffect(() => {
    if (triggerScroll && navigateToSectionId && !isLoading) {
      const reviewSection = document.getElementById(
        Constants.PATIENT_REVIEWS_SECTION
      );
      if (reviewSection) {
        reviewSection.scrollIntoView();
      }
      setTriggerScroll(false);
    }
  }, [triggerScroll, navigateToSectionId, isLoading]);

  const { initiateViewEstimate, searchBySpeciality } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      initiateViewEstimate: state.typeaheadSearchStore.initiateViewEstimate,
      searchBySpeciality: state.typeaheadSearchStore.searchBySpeciality,
    }))
  );

  const { portalData } = useContext(PortalContext);

  const providerDetails = data?.providerDetails?.providerDetails || {};

  const featureFlags = useLagoon('feature-flags')();
  const [showProviderBioFlag] = getFeatureFlags(featureFlags, [
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROVIDER_AI_BIO_UI,
  ]);

  const policyNumber = getPolicyNumber(currentMember, coverageType);
  const currentSelectedState = getStateFromSearchBar();

  const isGetAiBioEligibilityByState = getAiBioEligibility(
    policyNumber,
    currentSelectedState
  );

  const providerBioContent =
    providerDetails?.providerBio?.data?.generatedBiography ?? '';

  const canShowAiProviderBio =
    showProviderBioFlag &&
    isGetAiBioEligibilityByState &&
    providerBioContent.trim().length > 0;

  const providerLocations = providerDetails?.providerLocations || [];
  const nameAttrs = getNameAttrs(providerDetails);
  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );

  const [
    isLimitedAccessToProvider,
    enableSendFeedback,
    enableNewFeedbackFormDesignEarlyPayFlag,
    enableNewFeedbackFormDesignFlag,
    isSearchRelevancyEventTrackingEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS
      .LIMITED_ACCESS_TO_PROVIDER_BANNER_AND_SCHEDULE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUGGEST_AN_EDIT_FORM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
  ]);

  const enableNewFeedbackFormDesign = currentMember.isIFPEarlyPay
    ? enableNewFeedbackFormDesignEarlyPayFlag
    : enableNewFeedbackFormDesignFlag;

  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));

  useEffect(() => {
    if (
      providerDetails?.providerName &&
      searchFromTypeAhead &&
      !searchBySpeciality
    ) {
      adobeStandardSearchEvent({
        linkName,
        term: searchTerm,
        numberOfResults: 1,
        method: AdobeEventMethods.Predictive,
        customAttributesBlock: {
          searchLocation: getSearchLocation(storedLocation),
          providerType: internalConvertProviderTypeToAdobeType(
            ProviderTypes.PRACTITIONER
          ),
        },
      });
    }
  }, [providerDetails?.providerName, searchFromTypeAhead, searchBySpeciality]);

  useEffect(() => {
    if (showDirection) {
      setNavigateToDirections(true);
    }
  }, [showDirection]);

  // For lookup by PCP ID, if needed
  const [providerByPcpId, getProviderByPcpId] = useProviderResultsV2({});
  const providerIdByPcpId = providerByPcpId?.data?.providers?.[0]?.providerId;
  const providerCountByPcpId = providerByPcpId?.data?.totalCountPractitioners;
  useEffect(() => {
    const doLookupByPcpId = isDoLookupByPcpId(
      hasQueried,
      isLoading,
      providerDetails?.providerId,
      source
    );
    if (doLookupByPcpId) {
      setLookUpByPcpIdEnabled(true);
      getProviderByPcpId({
        providerType: ProviderTypes.PRACTITIONER,
        pcpID: storeProviderId,
      });
    } else if (isLoading) {
      setHasQueried(true);
    } else if (isLoading == false && hasQueried && !providerLocations?.length) {
      navigate(
        `${
          portalData?.portalConfig?.homePage || '/?chipValue=All'
        }#providerNotFound`
      );
    }
  }, [isLoading]);

  // Redirect to proper providerId if lookup by PCP ID succeeded
  useEffect(() => {
    if (!providerDetails?.providerId && providerIdByPcpId) {
      const urlParams = convertObjectToUrlParams(chipStore, {
        providerId: providerIdByPcpId,
        locationId,
      });
      navigate(`${ConstantsRoutes.PROVIDER_DETAILS.path}${urlParams}`);
    } else if (
      !providerIdByPcpId &&
      lookUpByPcpIdEnabled &&
      providerCountByPcpId === 0
    ) {
      navigate(
        `${
          portalData?.portalConfig?.homePage || '/?chipValue=All'
        }#providerNotFound`
      );
    }
  }, [providerIdByPcpId, providerCountByPcpId]);

  const selectedLocationIndex = getSelectedLocationIndex(
    providerLocations,
    selectedId
  );
  const selectedLocation = providerLocations?.[selectedLocationIndex];
  const networkStatus = getNetworkStatus(selectedLocation?.network?.status);

  const { providerType } = providerDetails;

  const locationTabIndex = providerType === ProviderType.ORGANIZATION ? 0 : 1;
  const [active, setActive] = useState(
    navigateToDirections ? locationTabIndex : 0
  );
  const handleTabChange = (index, isAnalyticsEnabled = true) => {
    setShowMaximumProviderLimitBanner(false);
    if (index !== 1) {
      setNavigateToDirections(false);
    }
    setActive(index);

    if (isAnalyticsEnabled) {
      adobeLinkTrackEvent({
        name: Object.values(Constants.PROVIDER_DETAILS)[index],
        location: `body:details tabs`,
        type: 'tab',
      });
    }
  };

  useEffect(() => {
    if (
      initiateViewEstimate &&
      portalData?.portalConfig?.userTier === LAWW_USER_TIER2
    ) {
      handleTabChange(3);
    } else if (navigateToDirections) {
      setActive(locationTabIndex);
    }
  }, [navigateToDirections]);

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [currentPageDetails] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CURRENT_PAGE_DETAILS,
    {}
  );

  const hitProviderSearchTelemetry = async () => {
    const providerList = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST
    );
    const selectedProviderIndex =
      storage.session.get(
        Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX
      ) + 1;
    const filteredResults = providerList
      .map((item) => {
        if (!item) return null; // Skip invalid items

        const providerType = item?.providerType?.toLowerCase();
        const address = item?.displayAddress
          ? item?.displayAddress
          : concatenateStrings(
              item?.address?.line?.join('') || '',
              item?.address?.city || '',
              item?.address?.district || '',
              item?.address?.state || '',
              item?.address?.postalCode || ''
            );

        return {
          resultId: item?.providerId || '',
          resultType:
            providerType === ProviderTypes?.PRACTITIONER
              ? 'PROVIDER'
              : providerType === ProviderTypes.MEDICAL_GROUP
              ? 'GROUP'
              : 'FACILITY',
          name: item?.providerName || item?.displayName || '',
          specialty: item?.speciality?.[0]?.category || item?.speciality || '',
          address,
          coverageType: Array.isArray(coverageType)
            ? coverageType?.map(getTelemetryCoverageType)
            : getTelemetryCoverageType(coverageType),
          rating: item?.healthGradeRating
            ? parseFloat(item?.healthGradeRating)
            : 0,
          numRatings: item?.surveyCount || 0,
          inNetworkLabel: item?.networkStatus || '',
          costIndicationLabel: item?.costIndicator || '',
        };
      })
      .filter(Boolean);
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      searchPlatform: 'web',
      timestamp: new Date().toISOString(),
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius ?? 0,
      },
      interpretedAsNameSearch: searchType !== 'speciality',
      viewType: 'other',
      results: filteredResults,
      resultPage: currentPageDetails?.pageNumber || 1,
      pageSize: providerList?.length,
      resultedInNullSearch: filteredResults?.length === 0,
      selectedResult: {
        resultId: providerDetails?.providerId,
        resultType:
          providerDetails?.providerType?.toLowerCase() ===
          ProviderTypes?.PRACTITIONER
            ? 'PROVIDER'
            : providerDetails?.providerType?.toLowerCase() ===
              ProviderTypes.MEDICAL_GROUP
            ? 'GROUP'
            : 'FACILITY',
        name: providerDetails?.providerName,
        specialty:
          Array.isArray(providerList[selectedProviderIndex - 1]?.speciality) &&
          providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            ? providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            : providerList[selectedProviderIndex - 1]?.speciality || '',
        address: concatenateStrings(
          providerDetails?.providerLocations[0]?.address?.line?.join(''),
          providerDetails?.providerLocations[0]?.address?.city,
          providerDetails?.providerLocations[0]?.address?.district,
          providerDetails?.providerLocations[0]?.address?.state,
          providerDetails?.providerLocations[0]?.address?.postalCode
        ),
        coverageType: Array.isArray(coverageType)
          ? coverageType?.map(getTelemetryCoverageType)
          : getTelemetryCoverageType(coverageType),
        rating:
          providerDetails?.healthGradeRating &&
          parseFloat(providerDetails?.healthGradeRating),
        numRatings: providerDetails?.surveyCount,
        inNetworkLabel: providerList[selectedProviderIndex - 1]?.networkStatus,
        costIndicationLabel:
          providerDetails?.providerLocations[0]?.costIndicator,
      },
      selectedResultOverallRank: selectedProviderIndex
        ? currentPageDetails?.pageNumber > 1
          ? (currentPageDetails?.pageNumber - 1) *
              currentPageDetails?.pageSize +
            selectedProviderIndex
          : selectedProviderIndex
        : 1,
      selectedResultOnCurrentPageRank: selectedProviderIndex,
      event: 'detail',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      providerDetails?.providerName &&
      !isLoading &&
      searchTerm &&
      searchTerm?.length > 0 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    }
  }, [providerDetails?.providerName, isLoading, searchTerm]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingSpinnerComp isLoading={isLoading} />
      ) : (
        <React.Fragment>
          <CardWrapperDetailsPage CSS={{ backgroundColor: '$bgHeaderColor' }}>
            {!(isMapView && mobileScreen) ? (
              <DetailsPageHeader
                avatar={avatar}
                coverageType={coverageType}
                dependentSeqNbr={dependentSeqNbr}
                enableNewFeedbackFormDesign={enableNewFeedbackFormDesign}
                enableSendFeedback={enableSendFeedback}
                feedbackFormType={
                  Constants.FEEDBACK_FORM_TYPES.PROVIDER_DETAILS
                }
                formProviderType={providerDetails?.providerType}
                handleTabChange={handleTabChange}
                hospitalBasedProviderIndicator={
                  selectedLocation?.hospitalBasedProviderIndicator
                }
                imageLocation={providerDetails?.imageLocation}
                inNetwork={networkStatus}
                isLimitedAccessToProvider={isLimitedAccessToProvider}
                isTieredDataCard={isTieredDataCardValue}
                nameAttrs={nameAttrs}
                networkId={providerDetails?.networkId}
                npi={providerDetails?.npi}
                practiceId={selectedLocation?.docasapPracticeId}
                primarySpeciality={providerDetails?.specialties}
                professionalDesignation={providerDetails?.primaryDegrees?.[0]}
                providerDetails={providerDetails}
                providerId={providerId || ''}
                providerLocations={providerLocations}
                providerName={providerDetails?.providerName}
                providerType={ProviderType.PRACTITIONER}
                rating={providerDetails?.healthGradeRating}
                sectionTypeValue={Constants.RESULT_SECTION.PROVIDER}
                setNavigateToDirections={setNavigateToDirections}
                surveyCount={providerDetails?.surveyCount}
                virtualCareOffered={selectedLocation?.virtualCareOffered}
              />
            ) : null}
          </CardWrapperDetailsPage>
          <CardWrapperDetailsPage CSS={{ backgroundColor: '$bgHeaderColor' }}>
            <ProviderDetailTabs
              active={active}
              canShowAiProviderBio={canShowAiProviderBio}
              chipValue={chipValue}
              coverageType={coverageType}
              handleTabChange={handleTabChange}
              isLimitedAccessToProvider={isLimitedAccessToProvider}
              isMapView={isMapView}
              isTiered={isTieredDataCard || isTieredSnackCard}
              locationKey={selectedLocation?.locationKey}
              navigateToDirections={navigateToDirections}
              preferencesData={preferencesData}
              providerBioContent={providerBioContent}
              providerDetails={providerDetails}
              setIsMapView={setIsMapView}
              setNavigateToDirections={setNavigateToDirections}
            />
          </CardWrapperDetailsPage>
        </React.Fragment>
      )}
      {enableSendFeedback && !enableNewFeedbackFormDesign && (
        <SendFeedback
          coverageType={coverageType}
          data={{
            name: providerDetails?.providerName,
            address: selectedLocation?.address,
            npi: providerDetails?.npi,
            mpin: providerDetails?.providerId,
            providerType: providerDetails?.providerType,
          }}
          type={Constants.FEEDBACK_FORM_TYPES.PROVIDER_DETAILS}
        />
      )}
    </React.Fragment>
  );
};
